import React from 'react';
import { FetchResult } from '@apollo/client';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from '@buyside/components/presentation/Button/button';
import CommentsContainer from '@buyside/components/container/CommentsContainer';
import ErrorBoundary from '@buyside/components/presentation/ErrorBoundary/errorBoundary';
import Like from '@buyside/components/presentation/Feed/Actions/like';

import { postUrl } from '@buyside/util/urlHelper';

import {
  EventObject,
  LikeEventMutation,
  Maybe,
  useLikeEventMutation
} from '@interfaces/graphql-typings';

import styles from './actionsStyles.scss';

const ActionsContainer: React.FC<{
  allowAnonymousComment: boolean;
  currentUserId: string;
  currentUserHasLiked: boolean;
  eventId?: string;
  eventObject?: EventObject;
  eventVerb?: string;
  feedItemId: string;
  hideLikes: boolean;
  isAllocator: boolean;
  isRetail: boolean;
  likeCount: number;
  profileImageUrl: Maybe<string>;
}> = ({
  allowAnonymousComment,
  currentUserHasLiked,
  currentUserId,
  eventId,
  eventObject,
  eventVerb,
  feedItemId,
  hideLikes,
  isAllocator,
  isRetail,
  likeCount,
  profileImageUrl
}): React.ReactElement => {
  const [commenting, openComment] = React.useState(false);

  const [likeEvent, { loading }] = useLikeEventMutation();

  const isPostType = eventObject?.__typename === 'Post';
  const isPostCommentType =
    eventObject?.__typename === 'Comment' && eventObject.commentObject?.__typename === 'Post';

  let commentsCount = 0;
  if (isPostType) {
    // @ts-ignore - eventObject is Post
    commentsCount = eventObject?.commentsCount || 0;
  } else if (isPostCommentType) {
    // @ts-ignore - eventObject is Comment, and commentObject is Post
    commentsCount = eventObject?.commentObject.commentsCount || 0;
  }

  const showComments = eventVerb !== 'PUBLISH_IDEA_UPDATE' && isPostType;

  return (
    <ErrorBoundary pageName="activity feed actions">
      <div className={styles.feedActionWrap} data-heap-segment="activity-feed-actions">
        {!hideLikes && eventId && (
          <Like
            currentUserHasLiked={currentUserHasLiked}
            isLoading={loading}
            likeCount={likeCount}
            onClick={(): Promise<FetchResult<LikeEventMutation>> => {
              // @ts-ignore
              if (window.hj) window.hj('tagRecording', ['activity_feed_like']);
              return likeEvent({ variables: { eventId } });
            }}
            id={`event-${eventId}-like`}
          />
        )}
        {isPostCommentType && (
          <Button
            className={styles.commentButton}
            faIcon={faComment}
            text={`Comment${commentsCount > 1 ? 's' : ''}${
              commentsCount > 0 ? ` (${commentsCount})` : ''
            }`}
            type={BUTTON_TYPES.LINK}
            size={BUTTON_SIZES.SMALL}
            // @ts-ignore - eventObject is Comment, and commentObject is Post
            url={postUrl(eventObject?.commentObject.id)}
            analyticsTag="comment-button"
          />
        )}
        {showComments && (
          <span className={styles.feedAction}>
            <Button
              className={styles.commentButton}
              faIcon={faComment}
              text={
                commenting
                  ? 'Close Comment'
                  : `Comment${commentsCount > 1 ? 's' : ''}${
                      commentsCount > 0 ? ` (${commentsCount})` : ''
                    }`
              }
              type={BUTTON_TYPES.LINK}
              size={BUTTON_SIZES.SMALL}
              onClick={(): void => openComment(!commenting)}
              analyticsTag="comment-button"
            />
            {commenting && (
              <CommentsContainer
                allowAnonymousComment={allowAnonymousComment}
                commentsCount={commentsCount}
                currentUserId={currentUserId}
                eventObject={eventObject}
                feedItemId={feedItemId}
                profileImageUrl={profileImageUrl || ''}
                isAllocator={isAllocator}
                isRetail={isRetail}
              />
            )}
          </span>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default ActionsContainer;
